import { Controller } from "@hotwired/stimulus"
import Chart from "chart.js/auto"
import "chartjs-adapter-date-fns"

export default class extends Controller {
  static values = {
    datasets: Array, // Cada item deve poder ter: { type, label, data, labels?, yAxisID? }
    arrowPath: String, // Caminho da imagem arrow.png
    arrowSpacing: Number // Espaçamento mínimo (px) entre setas da direção do vento
  }

  static targets = ["buttons"]

  static defaultColors = [
    "#EF4444", "#F97316", "#F59E0B", "#EAB308",
    "#84CC16", "#22C55E", "#10B981", "#14B8A6",
    "#06B6D4", "#0EA5E9", "#3B82F6", "#6366F1",
    "#8B5CF6", "#A855F7"
  ]

  connect() {
    this.visibleSet = new Set()
    this.buttonMap = new Map()
    this.allDatasets = []

    // === Configuração de eixos para Forecast Weather ===
    this.axisMeta = {
      "1": { title: "Power Forecast", unit: "kW/MW", position: "left" },
      "2": { title: "Wind", unit: "m/s", position: "right" },
      "3": { title: "Temperature", unit: "°C", position: "left" },
      "4": { title: "Precipitation", unit: "mm/h", position: "right" },
      "5": { title: "Specific Humidity", unit: "g/kg", position: "right" }
    }

    try {
      this.datasets = this.datasetsValue || []
    } catch (e) {
      console.error("Erro ao parsear datasetsValue:", e)
      return
    }

    const canvas = this.element.querySelector("#forecastWeatherChart")
    if (!canvas) {
      console.error("Canvas com ID #forecastWeatherChart não encontrado.")
      return
    }

    const initialLabels = Array.isArray(this.datasets) && this.datasets[0] && Array.isArray(this.datasets[0].labels)
      ? this.datasets[0].labels
      : []

    // Cria closures para usar nas callbacks do Chart.js
    const axisMeta = this.axisMeta
    const numberFormatter = (v) => this.formatNumber(v)
    const powerFormatter = (v) => this.formatPowerWithUnit(v)

    // Carrega a imagem da seta para direção do vento
    const arrowImage = new Image()
    arrowImage.src = this.arrowPathValue || "/arrow.png"
    
    // Flag para controlar se a imagem foi carregada
    let imageLoaded = false
    arrowImage.onload = () => {
      imageLoaded = true
    }
    arrowImage.onerror = () => {
      console.error("Erro ao carregar arrow.png:", arrowImage.src)
    }
    // Espaçamento mínimo opcional (em pixels) para desenhar as setas
    const minArrowSpacing = Number(this.arrowSpacingValue || 0)
    
    // Plugin para desenhar setas rotacionadas conforme a direção do vento
    const directionArrowPlugin = {
      id: "directionArrowPlugin",
      afterDatasetsDraw(chart) {
        const dsIndex = chart.data.datasets.findIndex(ds => ds && ds.label === "Wind Direction")
        if (dsIndex === -1) return

        const meta = chart.getDatasetMeta(dsIndex)
        if (!meta || !meta.data || meta.data.length === 0) return

        const dataset = chart.data.datasets[dsIndex]
        const ctx = chart.ctx
        const topY = chart.chartArea.top + 15 // altura fixa perto do topo do gráfico

        // Calcula um espaçamento alvo para reduzir sobreposição visual
        const chartWidth = (chart.chartArea && Number.isFinite(chart.chartArea.right - chart.chartArea.left))
          ? (chart.chartArea.right - chart.chartArea.left)
          : 0
        const spacing = (minArrowSpacing && Number.isFinite(minArrowSpacing) && minArrowSpacing > 0)
          ? minArrowSpacing
          : Math.max(16, chartWidth / 40) // ~40 setas no máximo, com mínimo de 16px

        // Tamanho da seta proporcional ao espaçamento, com limites
        const arrowSize = Math.max(8, Math.min(16, spacing * 0.8))

        // Verifica se a imagem foi carregada
        if (!imageLoaded || !arrowImage.complete || arrowImage.naturalWidth === 0) return

        ctx.save()
        let lastX = -Infinity
        meta.data.forEach((elem, i) => {
          const x = elem && Number.isFinite(elem.x) ? elem.x : NaN
          if (!Number.isFinite(x)) return

          // Pula se muito próximo do último ícone desenhado
          if (x - lastX < spacing) return

          const raw = dataset.data?.[i]
          const deg = typeof raw === "number" ? raw : Number(raw)
          if (!Number.isFinite(deg)) return

          // Rotaciona a seta conforme a direção (0° = Norte)
          const angle = (deg * Math.PI) / 180

          ctx.save()
          ctx.translate(x, topY)
          ctx.rotate(angle)
          
          // Desenha a imagem centralizada e rotacionada
          ctx.drawImage(
            arrowImage,
            -arrowSize / 2,
            -arrowSize / 2,
            arrowSize,
            arrowSize
          )
          
          ctx.restore()

          // Marca a última posição X desenhada
          lastX = x
        })
        ctx.restore()
      }
    }

    this.chart = new Chart(canvas.getContext("2d"), {
      type: "bar",
      data: {
        labels: initialLabels,
        datasets: []
      },
      plugins: [directionArrowPlugin],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false, axis: "x" },
        plugins: {
          legend: { position: "top" },
          tooltip: {
            callbacks: {
              // Adiciona unidade baseada no yAxisID do dataset
              label: function (context) {
                const dsLabel = context.dataset?.label || ""
                const yid = context.dataset?.yAxisID || "1"
                // Prefer parsed value (number) when available
                const raw = (typeof context.parsed?.y === "number") ? context.parsed.y : context.raw
                if (dsLabel.includes("Direction")) {
                  const deg = (typeof raw === "number") ? raw : Number(raw)
                  if (!Number.isFinite(deg)) return `${dsLabel}: –`
                  const cardinal = (() => {
                    const dirs = [
                      "N", "NNE", "NE", "ENE",
                      "E", "ESE", "SE", "SSE",
                      "S", "SSW", "SW", "WSW",
                      "W", "WNW", "NW", "NNW"
                    ]
                    const idx = Math.round(((deg % 360) / 22.5)) % 16
                    return dirs[idx]
                  })()
                  return `${dsLabel}: ${deg.toFixed(0)}° ${cardinal}`
                } else if (String(yid) === "1") {
                  const formatted = powerFormatter(raw)
                  return `${dsLabel}: ${formatted}`
                } else {
                  const unit = axisMeta[yid]?.unit || ""
                  const val = numberFormatter(raw)
                  return unit ? `${dsLabel}: ${val} ${unit}` : `${dsLabel}: ${val}`
                }
              }
            }
          },
          title: { display: false }
        },
        // === Várias escalas (uma por yAxisID) ===
        scales: {
          // Escala dedicada à direção (0–360°), sempre oculta
          dir: {
            type: "linear",
            position: "right",
            display: false,
            min: 0,
            max: 360,
            grid: { drawOnChartArea: false },
            title: { display: false }
          },
          "1": {
            type: "linear",
            position: axisMeta["1"]?.position || "left",
            title: { display: true, text: `${axisMeta["1"]?.title || "Axis 1"} (${axisMeta["1"]?.unit || ""})`.trim() },
            ticks: {
              callback: (value) => powerFormatter(value)
            }
          },
          "2": {
            type: "linear",
            position: axisMeta["2"]?.position || "right",
            grid: { drawOnChartArea: false },
            title: { display: true, text: `${axisMeta["2"]?.title || "Axis 2"} (${axisMeta["2"]?.unit || ""})`.trim() },
            ticks: {
              callback: (value) => {
                const v = numberFormatter(value)
                return axisMeta["2"]?.unit ? `${v} ${axisMeta["2"].unit}` : v
              }
            }
          },
          "3": {
            type: "linear",
            position: axisMeta["3"]?.position || "left",
            grid: { drawOnChartArea: true },
            title: { display: true, text: `${axisMeta["3"]?.title || "Axis 3"} (${axisMeta["3"]?.unit || ""})`.trim() },
            ticks: {
              callback: (value) => {
                const v = numberFormatter(value)
                return axisMeta["3"]?.unit ? `${v} ${axisMeta["3"].unit}` : v
              }
            }
          },
          "4": {
            type: "linear",
            position: axisMeta["4"]?.position || "right",
            grid: { drawOnChartArea: false },
            title: { display: true, text: `${axisMeta["4"]?.title || "Axis 4"} (${axisMeta["4"]?.unit || ""})`.trim() },
            ticks: {
              callback: (value) => {
                const v = numberFormatter(value)
                return axisMeta["4"]?.unit ? `${v} ${axisMeta["4"].unit}` : v
              }
            },
          },
          "5": {
            type: "linear",
            position: axisMeta["5"]?.position || "right",
            grid: { drawOnChartArea: false },
            title: { display: true, text: `${axisMeta["5"]?.title || "Axis 5"} (${axisMeta["5"]?.unit || ""})`.trim() },
            ticks: {
              callback: (value) => {
                const v = numberFormatter(value)
                return axisMeta["5"]?.unit ? `${v} ${axisMeta["5"].unit}` : v
              }
            }
          },
          x: {
            type: "time",
            time: {
              unit: "day",
              tooltipFormat: "yyyy-MM-dd HH:mm",
              displayFormats: { 
                hour: "HH:mm",
                day: "dd/MM" 
              }
            },
            ticks: { 
              autoSkip: true, 
              maxRotation: 0, 
              minRotation: 0,
              maxTicksLimit: 8,
              autoSkipPadding: 20,
              source: "auto"
            },
            offset: false,
            bounds: "data",
            grid: {
              offset: false
            },
            title: { display: false }
          }
        }
      }
    })

    this.loadDatasets()
  }

  loadDatasets() {
    const colors = this.constructor.defaultColors
    const valid = (this.datasets || []).filter(d =>
      d && d.type && d.label && Array.isArray(d.data)
    )

    if (valid.length === 0) {
      console.error("Nenhum dataset válido foi encontrado.")
      return
    }

    // Detecta se é um único dia antes de configurar o chart
    const firstDataset = valid[0]
    const labels = firstDataset?.labels || []
    let isSingleDay = false
    if (labels.length >= 2) {
      try {
        const first = new Date(labels[0])
        const last = new Date(labels[labels.length - 1])
        const diffHours = (last - first) / (1000 * 60 * 60)
        isSingleDay = diffHours <= 24
      } catch {
        isSingleDay = false
      }
    }

    // Atualiza as configurações do eixo X baseado no período
    this.chart.options.scales.x.time.unit = isSingleDay ? "hour" : "day"
    this.chart.options.scales.x.ticks.maxTicksLimit = isSingleDay ? 12 : 8

    // Utilitário para normalizar arrays de dados em números ou null
    const normalizeDataArray = (arr) => (arr || []).map(v => {
      // Se já é formato {x, y}, mantém assim
      if (v && typeof v === "object" && ("x" in v) && ("y" in v)) {
        return { x: v.x, y: (typeof v.y === "number" && Number.isFinite(v.y)) ? v.y : null }
      }
      // Senão normaliza como número
      if (v === null || v === undefined) return null
      if (typeof v === "number" && Number.isFinite(v)) return v
      const n = Number(v)
      return Number.isNaN(n) ? null : n
    })

    // Normaliza datasets, aplicando cores e garantindo yAxisID (default "1" se não vier)
    this.allDatasets = valid.map((item, index) => {
      let color = colors[index % colors.length]
      const yAxisID = item.yAxisID ? String(item.yAxisID) : "1"

      // Cores fixas: Power = vermelho, Wind Speed = verde
      if (item.label === "Power Forecast") {
        color = "#EF4444" // red
      } else if (item.label === "Wind Speed") {
        color = "#22C55E" // green
      }

      // Remove pontos das linhas de dados reais
      const isRealData = item.label === "Real Power" || item.label === "Real Wind Speed"

      const normalizedData = normalizeDataArray(item.data)

      if (item.label === "Wind Direction") {
        // Oculta a linha/ pontos visuais; ícones serão renderizados pelo plugin
        return {
          ...item,
          type: "line",
          yAxisID: "dir",
          data: normalizedData,
          borderColor: "rgba(0,0,0,0)",
          pointBackgroundColor: "rgba(0,0,0,0)",
          backgroundColor: "rgba(0,0,0,0)",
          pointRadius: 0,
          fill: false,
          tension: 0,
          order: valid.length - index
        }
      } else if (item.type === "bar") {
        return {
          ...item,
          type: "bar",
          yAxisID,
          data: normalizedData,
          backgroundColor: item.backgroundColor || color,
          borderColor: item.borderColor || color,
          order: valid.length - index
        }
      } else if (item.type === "line") {
        return {
          ...item,
          type: "line",
          yAxisID,
          data: normalizedData,
          borderColor: item.borderColor || color,
          pointBackgroundColor: item.pointBackgroundColor || color,
          backgroundColor: item.backgroundColor || `${color}33`,
          pointRadius: isRealData ? 0 : undefined,
          borderDash: item.borderDash || [],
          fill: false,
          tension: 0.3,
          order: valid.length - index
        }
      }
      return { ...item, yAxisID, data: normalizedData, borderDash: item.borderDash || [], order: valid.length - index }
    })

    // Labels do gráfico (usa labels do 1º dataset se existir)
    if (Array.isArray(this.allDatasets[0].labels)) {
      this.chart.data.labels = this.allDatasets[0].labels
    }

    const availableLabels = this.allDatasets.map(ds => ds.label)
    const selectedLabels = this.loadVisibleLabels(availableLabels)

    selectedLabels.forEach(label => this.visibleSet.add(label))
    this.chart.data.datasets = this.allDatasets.filter(ds => this.visibleSet.has(ds.label))

    // Garante que sempre exista pelo menos uma série visível.
    if (this.chart.data.datasets.length === 0 && this.allDatasets.length > 0) {
      const fallback = this.allDatasets[0].label
      this.visibleSet.add(fallback)
      this.chart.data.datasets = [ this.allDatasets[0] ]
    }

    this.updateAxisVisibility()
    this.updateWindAxisMax()
    this.chart.update()
    this.saveVisibleLabelsToStorage()

    // Botões de toggle para TODOS os datasets (bar e line), incluindo o primeiro
    this.buttonsTarget.innerHTML = ""
    this.allDatasets.forEach((ds) => {
      const li = document.createElement("li")
      li.textContent = ds.label
      li.dataset.label = ds.label
      li.className = this.buttonClass(this.visibleSet.has(ds.label))
      li.addEventListener("click", () => this.toggleDataset(ds, li))
      this.buttonsTarget.appendChild(li)
      this.buttonMap.set(ds.label, li)
    })
  }

  toggleDataset(dataset, liElement) {
    const label = dataset.label

    if (this.visibleSet.has(label)) {
      this.visibleSet.delete(label)
    } else {
      this.visibleSet.add(label)
    }
 
    // Reconstrói datasets visíveis
    this.chart.data.datasets = this.allDatasets.filter(ds =>
      this.visibleSet.has(ds.label)
    )

    // Atualiza visibilidade dos eixos
    this.updateAxisVisibility()
    this.updateWindAxisMax()

    // Atualiza estilos de TODOS os botões
    this.buttonMap.forEach((button, btnLabel) => {
      button.className = this.buttonClass(this.visibleSet.has(btnLabel))
    })

    this.saveVisibleLabelsToStorage()
    this.chart.update()
  }

  defaultVisibleLabels(availableLabels = []) {
    const defaults = ["Wind Speed", "Power Forecast", "Real Power", "Real Wind Speed"]
    return defaults.filter(label => availableLabels.includes(label))
  }

  storageKey() {
    return "forecast-weather:selected-datasets:v1"
  }

  loadVisibleLabels(availableLabels = []) {
    const fallback = this.defaultVisibleLabels(availableLabels)

    try {
      const raw = localStorage.getItem(this.storageKey())
      if (!raw) return fallback

      const saved = JSON.parse(raw)
      if (!Array.isArray(saved)) return fallback

      const normalized = saved.filter(label => availableLabels.includes(label))
      return normalized.length > 0 ? normalized : fallback
    } catch (_error) {
      return fallback
    }
  }

  saveVisibleLabelsToStorage() {
    try {
      localStorage.setItem(this.storageKey(), JSON.stringify(Array.from(this.visibleSet)))
    } catch (_error) {
      // Ignora indisponibilidade de storage (ex.: modo privado/restrições do navegador)
    }
  }

  buttonClass(isVisible) {
    const base = "border border-gray-200 rounded-lg mb-1 px-2 py-1 text-xs cursor-pointer select-none transition"
    const inactive = "hover:bg-gray-100 text-gray-700"
    const activeCls = "bg-enercon-green-1 text-white"
    return `${base} ${isVisible ? activeCls : inactive}`.trim()
  }

  formatNumber(value) {
    if (typeof value !== "number" || isNaN(value)) {
      return "–"
    }
    if (Math.abs(value) >= 1000) {
      return (value / 1000).toFixed(1) + "k"
    }
    return value.toFixed(1)
  }

  // Formata potência: mostra em MW quando |kW| >= 1000; caso contrário em kW
  formatPowerWithUnit(value) {
    if (typeof value !== "number" || isNaN(value)) {
      return "–"
    }
    const sign = value < 0 ? "-" : ""
    const abs = Math.abs(value)
    if (abs >= 1000) {
      const mw = abs / 1000
      const num = mw >= 10 ? mw.toFixed(0) : mw.toFixed(1)
      return `${sign}${num} MW`
    }
    return `${sign}${abs.toFixed(1)} kW`
  }

  updateAxisVisibility() {
    // Determina quais eixos estão sendo usados
    const usedAxes = new Set()
    this.chart.data.datasets.forEach(ds => {
      if (ds.yAxisID) {
        usedAxes.add(ds.yAxisID)
      }
    })

    // Atualiza a visibilidade de cada eixo
    Object.keys(this.chart.options.scales).forEach(axisKey => {
      if (axisKey === "x" || axisKey === "dir") return // Pula eixo X e mantém direção oculta

      const shouldDisplay = usedAxes.has(axisKey)
      this.chart.options.scales[axisKey].display = shouldDisplay
    })
  }

  // Define o máximo do eixo de vento (yAxisID "2") como 20 m/s
  // ou o maior valor das séries visíveis desse eixo, o que for maior
  updateWindAxisMax() {
    if (!this.chart || !this.chart.options || !this.chart.options.scales) return
    const scale = this.chart.options.scales["2"]
    if (!scale) return

    let maxVal = 0
    for (const ds of this.chart.data.datasets || []) {
      if (String(ds.yAxisID || "") === "2" && Array.isArray(ds.data)) {
        for (const v of ds.data) {
          const num = typeof v === "number" ? v : Number(v)
          if (!Number.isNaN(num)) {
            if (num > maxVal) maxVal = num
          }
        }
      }
    }

    const desiredMax = Math.max(20, maxVal)
    // Aplica como limite rígido para refletir exatamente a regra
    scale.max = desiredMax
    // Mantém início em zero para melhor leitura (opcional)
    if (scale.min == null) scale.min = 0
  }
}
